/**
 * Kjøretøy langs rute (three.js custom layer).
 *
 * Viser en GLB-modell (bussen) som plasseres og orienteres langs ruta via
 * route-sampleren. `setDistance(meter)` flytter den; retningen tas fra rutas
 * bearing der den står, så bussen alltid peker i kjøreretningen.
 *
 * Modell: ruter_bus_stylized.glb – 12,5 m lang, +Y opp, front mot +Z, anker
 * på bakkenivå-senter (fra manifest). Fronten (+Z) roteres til å peke langs
 * ruta.
 */

import {
  Camera,
  Scene,
  WebGLRenderer,
  DirectionalLight,
  AmbientLight,
  Matrix4,
  Vector3,
  Group,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MercatorCoordinate } from 'maplibre-gl';

// Grader lagt til (−bearing) for at bussens front skal følge kjøreretningen.
// Skru på denne om bussen står skjevt (90 satte den langs veien; finjuster her).
const HEADING_OFFSET_DEG = 2;

/**
 * @param {object} opts
 * @param {string} opts.id
 * @param {string} opts.url                 GLB-url
 * @param {object} opts.sampler             route-sampler (bus)
 * @param {number} [opts.scale=1]           ekstra skalering (1 = ekte meter)
 * @param {number} [opts.exaggeration=1]    terreng-eksagerering (høyde × denne)
 * @param {number} [opts.minzoom]
 * @param {function} [opts.onWheels]        (root)=>void – finn hjul-noder ved last
 */
export function createVehicleLayer({
  id,
  url,
  sampler,
  scale = 1,
  exaggeration = 1,
  minzoom,
}) {
  let renderer;
  let scene;
  let camera;
  let map;
  let model = null; // gltf.scene
  let hidden = false;

  // Gjeldende plassering langs ruta.
  let merc = MercatorCoordinate.fromLngLat(sampler.at(0).lngLat, 0);
  let meterScale = merc.meterInMercatorCoordinateUnits();
  let headingRad = 0; // rotasjon om opp-aksen, fra rutas bearing

  // +Y-opp (glTF) → +Z-opp (mercator): roter +90° om X. Speil på X som de
  // andre modellene (mercator-Y peker sør).
  const rotationX = new Matrix4().makeRotationAxis(new Vector3(1, 0, 0), Math.PI / 2);
  const mirror = new Matrix4().makeScale(-1, 1, 1);

  const layer = {
    id,
    type: 'custom',
    renderingMode: '3d',

    /** Flytt bussen til `d` meter fra rutestart. Returnerer samplerpunktet. */
    setDistance(d) {
      const p = sampler.at(d);
      const groundZ = (p.elevation ?? 0) * exaggeration;
      merc = MercatorCoordinate.fromLngLat(p.lngLat, groundZ);
      meterScale = merc.meterInMercatorCoordinateUnits();
      // bearing: 0 = nord, 90 = øst (kompass). Modellens front peker mot +Z.
      // HEADING_OFFSET_DEG justerer så fronten følger kjøreretningen langs
      // ruta (ett tall å skru på om bussen står skjevt).
      headingRad = ((p.bearing + HEADING_OFFSET_DEG) * Math.PI) / 180;
      hidden = p.structure === 'tunnel'; // skjul i tunnel
      if (map) map.triggerRepaint();
      return p;
    },

    setHidden(v) { hidden = v; if (map) map.triggerRepaint(); },

    onAdd(mapInstance, gl) {
      map = mapInstance;
      camera = new Camera();
      scene = new Scene();

      scene.add(new AmbientLight(0xffffff, 1.0));
      const sun = new DirectionalLight(0xffe6c2, 2.8);
      sun.position.set(-0.6, -0.4, 1).normalize();
      scene.add(sun);
      const fill = new DirectionalLight(0xdfe6ea, 1.2);
      fill.position.set(0.5, 0.5, 0.8).normalize();
      scene.add(fill);

      renderer = new WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
      renderer.autoClear = false;

      new GLTFLoader().load(
        url,
        (gltf) => {
          model = new Group();
          model.add(gltf.scene);
          scene.add(model);
          map.triggerRepaint();
        },
        undefined,
        (err) => console.error(`[vehicle:${id}] kunne ikke laste ${url}`, err),
      );
    },

    render(gl, options) {
      if (hidden || !model) return;
      if (minzoom != null && map.getZoom() < minzoom) return;

      const mvp =
        options?.defaultProjectionData?.mainMatrix ??
        options?.modelViewProjectionMatrix ??
        options;
      const m = new Matrix4().fromArray(mvp);

      const s = meterScale * scale;
      const translate = new Matrix4().makeTranslation(merc.x, merc.y, merc.z);
      const scaleM = new Matrix4().makeScale(s, s, s);
      const heading = new Matrix4().makeRotationAxis(new Vector3(0, 1, 0), headingRad);

      // translate · scale · rotationX · heading · mirror
      const modelMatrix = translate
        .multiply(scaleM)
        .multiply(rotationX)
        .multiply(heading)
        .multiply(mirror);

      camera.projectionMatrix = m.multiply(modelMatrix);
      renderer.resetState();
      gl.clear(gl.DEPTH_BUFFER_BIT);
      renderer.render(scene, camera);
    },
  };

  return layer;
}
